<template>
  <!-- The right column while a contract is picked on its third party's page (US 9369 + 9371): the third party,
       which stays put when you switch contracts -->
  <div class="pp-side" qid="vendor-side-summary">
    <div class="pp-colhead">
      <span class="pp-scope is-par"><font-awesome-icon icon="building"/>{{ $t('vendor_merged.third_party') }}</span>
      <b>{{ vendor.name }}</b>
      <span class="pp-colhead__line"></span>
    </div>

    <template v-if="summary">
      <!-- Where it takes our data, across all its contracts -->
      <section v-if="modules.gdpr && summary.countries.length" class="v2-card sp-side__card" qid="vendor-side-data">
        <h4><font-awesome-icon icon="globe"/>{{ $t('vendor_page.data.title', { name: vendor.name }) }}</h4>
        <span class="pp-scope is-par pp-side__scope"><font-awesome-icon icon="building"/>{{ $t('vendor_merged.all_contracts') }}</span>
        <div v-for="country in summary.countries" :key="country.name" class="sp-row" :class="{ 'is-warn': needsBasis(country) }">
          <span><img v-if="country.flag" :src="country.flag" height="11" class="cp-flag" alt="">{{ country.name }}</span>
          <span v-if="country.eea === false">
            <span v-for="basis in country.basis" :key="basis" class="sp-badge" :class="isValidBasis(basis) ? 'is-ok' : 'is-warn'">{{ basisLabel(basis) }}</span>
            <span v-if="!country.basis.length" class="sp-badge is-warn">{{ $t('vendor_page.data.no_basis') }}</span>
          </span>
          <span v-else-if="country.eea" class="sp-muted">{{ $t('vendor_page.data.eea') }}</span>
        </div>
      </section>

      <!-- The company -->
      <section class="v2-card sp-side__card" qid="vendor-side-company">
        <h4>
          <font-awesome-icon icon="building"/>{{ $t('vendor_page.company.title') }}
          <span class="sp-grow"></span>
          <button v-if="canEdit" type="button" class="v2-link pp-side__edit" qid="vendor-side-edit" @click="$emit('edit')">
            <font-awesome-icon icon="pen"/>{{ $t('system_hero.edit') }}
          </button>
        </h4>
        <div v-for="row in companyRows" :key="row.key" class="sp-row" :class="{ 'is-warn': !row.value }">
          <span class="sp-muted">{{ row.label }}</span>
          <span>{{ row.value || $t('contract_page.data.no_locations') }}</span>
        </div>
      </section>

      <!-- Where it's used -->
      <section class="v2-card sp-side__card" qid="vendor-side-used">
        <h4><font-awesome-icon icon="link"/>{{ $t('system_page.used.title') }}</h4>
        <ul class="sp-used">
          <li v-for="item in usedItems" :key="item.key" :class="'is-' + item.module">
            <b>{{ item.count }}</b>{{ $tc(item.label, item.count) }}
          </li>
        </ul>
        <button type="button" class="v2-link sp-open" qid="vendor-side-open" @click="$emit('open-vendor')">
          {{ $t('vendor_merged.open_vendor', { name: vendor.name }) }} <font-awesome-icon icon="arrow-right"/>
        </button>
      </section>
    </template>
    <div v-else class="v2-card sp-side__card cp-loading"><span class="sx-skel is-short"></span><span class="sx-skel is-long"></span></div>
  </div>
</template>

<script>
  import {isValidBasis, basisLabel} from '@/util/transferBasis.js';

  export default {
    name: 'VendorSideSummary',
    props: {
      vendor: { type: Object, required: true },
      // The third party's overview (vendors/{slug}/overview), loaded by its own Overview tab
      summary: { type: Object, default: null }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      modules() {
        let client = this.getClient;
        return {
          gdpr: !!client.gdprModule,
          subProcessors: !!client.gdprAdvancedModule,
          systems: !!client.systemModule,
          activities: !!client.processingActivitiesModule,
          risk: !!client.riskModule
        };
      },
      canEdit() {
        return this.isGranted([this.permissions.THIRD_PARTY_EDIT, this.permissions.THIRD_PARTY_EDIT_OWN], this.vendor)
          && (!this.getClient.parent || this.vendor.partition);
      },
      // The rows the third party needs, in short
      companyRows() {
        let vendor = this.vendor;
        let place = [vendor.postal_code, vendor.city].filter(Boolean).join(' ');
        return [
          { key: 'address', label: this.$t('administration.clients.create.street_label'), value: [vendor.address, place].filter(Boolean).join(', ') },
          { key: 'country', label: this.$t('administration.clients.create.country_label'), value: vendor.country },
          { key: 'company_number', label: this.$t('administration.clients.create.cvr_label'), value: vendor.company_number }
        ];
      },
      usedItems() {
        let counts = this.summary.counts;
        let modules = this.modules;
        return [
          { key: 'contracts', label: 'vendor_page.used.contracts', count: counts.contracts, module: 'gdpr', show: true },
          { key: 'systems', label: 'contract_page.used.systems', count: counts.systems, module: 'gdpr', show: modules.systems },
          { key: 'activities', label: 'contract_page.used.activities', count: counts.activities, module: 'gdpr', show: modules.activities },
          { key: 'sub_processors', label: 'contract_page.used.sub_processors', count: counts.sub_processors, module: 'gdpr', show: modules.subProcessors },
          { key: 'risks', label: 'contract_page.used.risks', count: counts.risks, module: 'grc', show: modules.risk }
        ].filter(item => item.show);
      }
    },
    methods: {
      needsBasis(country) {
        return country.eea === false && (!country.basis.length || !country.basis.every(isValidBasis));
      },
      isValidBasis(basis) {
        return isValidBasis(basis);
      },
      basisLabel(basis) {
        return basisLabel(this, basis);
      }
    }
  }
</script>
